import parse from 'html-react-parser';
import { useEffect, useState } from 'react';

import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/core/Accordion';
import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization}  from '@/hooks/useOrganization';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { getFilter } from '@/services/angularjs-bridge';

import { GalleryMode, ItemAttribute } from '../export-media-asset.types';
import { MediaAttribute } from './MediaAttribute';

interface ItemsMediaProps {
    itemAttributes: ItemAttribute[];
    itemPrimaryKey: string;
    onAssetSelect: (item: ItemAttribute) => void;
    galleryMode: GalleryMode;
}

const ItemsMedia = ({ itemAttributes, itemPrimaryKey, onAssetSelect, galleryMode }: ItemsMediaProps) => {

    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const { dataModel } = useDataModel();
    const [displayAttributes, setDisplayAttributes] = useState<string[]>();
    const { organization } = useOrganization();
    const { user } = useUser();

    useEffect(() => {

        if (!dataModel) {
            return;
        }

        const layout = dataModel.layout('media_asset');
        if (layout) {
            let title = itemPrimaryKey;

            if (layout.layoutOptions && layout.layoutOptions.itemTitleFilter) {
                const filterName = layout.layoutOptions.itemTitleFilter;
                const titleFilterFn = getFilter(filterName);
                title = titleFilterFn(itemAttributes[0], user, organization);
            }

            setTitle(title);

            if (layout.sections.length > 0) {
                // We grab the display attributes for an asset from the first
                // section in the layout, as they are identical in the other sections.

                const displayAttributes =
                    layout.sections[0].attributes
                        // Display attributes are the ones with `hidden` set as false
                        // not true or undefined, just simply FALSE!
                        .filter(attr => attr.name && attr.hidden === false)
                        .map(attr => attr.name);

                setDisplayAttributes(displayAttributes);
            }
        }

    }, [itemAttributes, dataModel, itemPrimaryKey, organization, user]);

    const getCountOfSelectedAssets = () => {
        return itemAttributes
            .filter(attribute => attribute.__selected)
            .length;
    };

    return (
        <AccordionItem value={itemPrimaryKey} className="tw-shadow tw-px-4 tw-mt-4 tw-bg-white">
            <AccordionTrigger>
                <span>{parse(title)}</span>
                <span className="tw-ml-auto tw-mr-4 tw-text-t-sm">
                    {getCountOfSelectedAssets()} {t('MEDIA_ASSET_EXPORT.OUT_OF')} {itemAttributes.length} {t('MEDIA_ASSET_EXPORT.SELECTED')}
                </span>
            </AccordionTrigger>

            <AccordionContent>
                <div className="tw-flex tw-flex-row tw-flex-wrap">
                    {itemAttributes.map((attribute, index) => {
                        return (
                            <MediaAttribute
                                key={itemPrimaryKey + index}
                                displayAttributes={displayAttributes}
                                attribute={attribute}
                                onAssetSelect={onAssetSelect}
                                galleryMode={galleryMode}
                            />
                        );
                    })}
                </div>
            </AccordionContent>

        </AccordionItem>
    );
};

export { ItemsMedia };
